// src/services/flipCardService.js
import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDocs, 
  getDoc,
  query, 
  where, 
  orderBy,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../config/firebase';

class FlipCardService {
  constructor() {
    this.booksCollection = 'pb-books';
  }

  // ============ FLIP CARD CONTENT MANAGEMENT ============
  
  async createCardContent(bookId, galleryId, contentData) {
    try {
      // Store card content as subcollection under gallery (matching media structure)
      const contentRef = collection(db, this.booksCollection, bookId, 'galleries', galleryId, 'card-content');
      const docRef = await addDoc(contentRef, {
        ...contentData,
        galleryId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      
      return { id: docRef.id, ...contentData };
    } catch (error) {
      console.error('Error creating card content:', error);
      throw error;
    }
  }

  async getCardContent(bookId, galleryId, contentId) {
    try {
      const contentRef = doc(db, this.booksCollection, bookId, 'galleries', galleryId, 'card-content', contentId);
      const docSnap = await getDoc(contentRef);
      
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() };
      }
      return null;
    } catch (error) {
      console.error('Error getting card content:', error);
      throw error;
    }
  }

  async updateCardContent(bookId, galleryId, contentId, updates) {
    try {
      const contentRef = doc(db, this.booksCollection, bookId, 'galleries', galleryId, 'card-content', contentId);
      await updateDoc(contentRef, {
        ...updates,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating card content:', error);
      throw error;
    }
  }

  async deleteCardContent(bookId, galleryId, contentId) {
    try {
      const contentRef = doc(db, this.booksCollection, bookId, 'galleries', galleryId, 'card-content', contentId);
      await deleteDoc(contentRef);
    } catch (error) {
      console.error('Error deleting card content:', error);
      throw error;
    }
  }

  // Get all card content for a gallery
  async getGalleryCardContent(bookId, galleryId) {
    try {
      const contentRef = collection(db, this.booksCollection, bookId, 'galleries', galleryId, 'card-content');
      const q = query(contentRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting gallery card content:', error);
      throw error;
    }
  }

  // ============ MEDIA ITEM LINKING ============
  
  async linkCardContentToMedia(bookId, galleryId, mediaId, contentId) {
    try {
      // Update media item to reference the card content
      const mediaRef = doc(db, this.booksCollection, bookId, 'galleries', galleryId, 'media', mediaId);
      await updateDoc(mediaRef, {
        backContentId: contentId,
        isFlippable: true,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error linking card content to media:', error);
      throw error;
    }
  }

  async unlinkCardContentFromMedia(bookId, galleryId, mediaId) {
    try {
      const mediaRef = doc(db, this.booksCollection, bookId, 'galleries', galleryId, 'media', mediaId);
      await updateDoc(mediaRef, {
        backContentId: null,
        isFlippable: false,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error unlinking card content from media:', error);
      throw error;
    }
  }

  // ============ CONTENT TEMPLATES ============
  
  getContentTemplate(type) {
    const templates = {
      form: {
        type: 'form',
        title: 'Contact Form',
        description: 'Get in touch with us',
        formType: 'contact',
        fields: [
          { 
            id: 'name', 
            label: 'Name', 
            type: 'text', 
            required: true,
            placeholder: 'Your full name'
          },
          { 
            id: 'email', 
            label: 'Email', 
            type: 'email', 
            required: true,
            placeholder: 'your.email@example.com'
          },
          { 
            id: 'message', 
            label: 'Message', 
            type: 'textarea', 
            required: true,
            placeholder: 'Your message...'
          }
        ],
        submitText: 'Send Message',
        successMessage: 'Thank you! Your message has been sent.',
        styling: {
          backgroundColor: '#ffffff',
          borderColor: '#e5e7eb',
          submitButtonColor: '#3b82f6'
        }
      },
      
      article: {
        type: 'article',
        title: 'Article Title',
        description: 'Article summary or excerpt',
        content: '<p>Write your article content here using rich text formatting...</p>',
        author: '',
        publishedAt: new Date(),
        readTime: 5,
        tags: [],
        featuredImage: '',
        styling: {
          backgroundColor: '#f9fafb',
          textColor: '#1f2937',
          accentColor: '#6b7280'
        }
      },
      
      quiz: {
        type: 'quiz',
        title: 'Quick Quiz',
        description: 'Test your knowledge',
        timeLimit: null, // null = no time limit
        showResults: true,
        allowRetry: true,
        questions: [
          {
            id: 'q1',
            question: 'What is the capital of South Africa?',
            type: 'multiple-choice',
            options: ['Cape Town', 'Johannesburg', 'Pretoria', 'All of the above'],
            correctAnswer: 3,
            explanation: 'South Africa has three capital cities, but Pretoria is the executive capital.'
          }
        ],
        styling: {
          backgroundColor: '#faf5ff',
          questionColor: '#7c3aed',
          correctColor: '#10b981'
        }
      },
      
      product: {
        type: 'product',
        title: 'Product Name',
        description: 'Product description and key features',
        price: 0,
        currency: 'ZAR',
        inStock: true,
        sku: '',
        category: '',
        features: [],
        specifications: {},
        images: [],
        actions: [
          {
            type: 'url',
            label: 'View Details',
            url: '',
            icon: 'external'
          },
          {
            type: 'cart',
            label: 'Add to Cart',
            icon: 'cart'
          }
        ],
        styling: {
          backgroundColor: '#f0fdf4',
          priceColor: '#059669',
          buttonColor: '#10b981'
        }
      },
      
      event: {
        type: 'event',
        title: 'Event Name',
        description: 'Event details and description',
        date: new Date(),
        endDate: null,
        location: '',
        address: '',
        capacity: 100,
        availableSpots: 100,
        price: 0,
        currency: 'ZAR',
        organizer: '',
        contact: '',
        tags: [],
        actions: [
          {
            type: 'booking',
            label: 'Book Now',
            icon: 'calendar'
          },
          {
            type: 'url',
            label: 'More Info',
            icon: 'external'
          }
        ],
        styling: {
          backgroundColor: '#fef2f2',
          dateColor: '#dc2626',
          buttonColor: '#ef4444'
        }
      },

      survey: {
        type: 'survey',
        title: 'Quick Survey',
        description: 'Help us improve by sharing your thoughts',
        isAnonymous: true,
        fields: [
          {
            id: 'rating',
            label: 'How would you rate this?',
            type: 'rating',
            required: true,
            maxRating: 5
          },
          {
            id: 'feedback',
            label: 'Additional feedback',
            type: 'textarea',
            required: false,
            placeholder: 'Optional feedback...'
          }
        ],
        styling: {
          backgroundColor: '#fffbeb',
          accentColor: '#f59e0b'
        }
      }
    };
    
    return templates[type] || templates.form;
  }

  // ============ ANALYTICS ============
  
  async trackFlipInteraction(bookId, galleryId, mediaId, isFlipped) {
    try {
      const analyticsRef = collection(db, this.booksCollection, bookId, 'galleries', galleryId, 'analytics');
      await addDoc(analyticsRef, {
        type: 'card_flip',
        mediaId,
        isFlipped,
        timestamp: serverTimestamp(),
        userAgent: navigator.userAgent,
        sessionId: this.getSessionId()
      });
    } catch (error) {
      console.error('Error tracking flip interaction:', error);
    }
  }

  async trackContentInteraction(bookId, galleryId, contentId, interactionType, data = {}) {
    try {
      const analyticsRef = collection(db, this.booksCollection, bookId, 'galleries', galleryId, 'analytics');
      await addDoc(analyticsRef, {
        type: 'content_interaction',
        contentId,
        interactionType, // 'form_submit', 'quiz_complete', 'product_view', etc.
        data,
        timestamp: serverTimestamp(),
        userAgent: navigator.userAgent,
        sessionId: this.getSessionId()
      });
    } catch (error) {
      console.error('Error tracking content interaction:', error);
    }
  }

  // ============ FORM RESPONSES ============
  
  async submitFormResponse(bookId, galleryId, contentId, formData) {
    try {
      const responsesRef = collection(db, this.booksCollection, bookId, 'galleries', galleryId, 'form-responses');
      await addDoc(responsesRef, {
        contentId,
        responses: formData,
        submittedAt: serverTimestamp(),
        userAgent: navigator.userAgent,
        sessionId: this.getSessionId()
      });

      // Track the interaction
      await this.trackContentInteraction(bookId, galleryId, contentId, 'form_submit', {
        fieldsCompleted: Object.keys(formData).length
      });

    } catch (error) {
      console.error('Error submitting form response:', error);
      throw error;
    }
  }

  // ============ UTILITIES ============
  
  getSessionId() {
    let sessionId = sessionStorage.getItem('pb-session-id');
    if (!sessionId) {
      sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      sessionStorage.setItem('pb-session-id', sessionId);
    }
    return sessionId;
  }

  // Bulk operations for gallery management
  async duplicateCardContent(bookId, sourceGalleryId, targetGalleryId, contentId) {
    try {
      const content = await this.getCardContent(bookId, sourceGalleryId, contentId);
      if (!content) throw new Error('Content not found');
      
      const { id, createdAt, updatedAt, ...contentData } = content;
      return await this.createCardContent(bookId, targetGalleryId, contentData);
    } catch (error) {
      console.error('Error duplicating card content:', error);
      throw error;
    }
  }

  async moveCardContent(bookId, sourceGalleryId, targetGalleryId, contentId) {
    try {
      const content = await this.getCardContent(bookId, sourceGalleryId, contentId);
      if (!content) throw new Error('Content not found');
      
      const { id, createdAt, updatedAt, ...contentData } = content;
      
      // Create in target gallery
      const newContent = await this.createCardContent(bookId, targetGalleryId, {
        ...contentData,
        galleryId: targetGalleryId
      });
      
      // Delete from source gallery
      await this.deleteCardContent(bookId, sourceGalleryId, contentId);
      
      return newContent;
    } catch (error) {
      console.error('Error moving card content:', error);
      throw error;
    }
  }

  async linkContentToMedia(bookId, galleryId, mediaId, contentId) {
    try {
      const mediaRef = doc(db, 'pb-books', bookId, 'galleries', galleryId, 'media', mediaId);
      await updateDoc(mediaRef, {
        backContentId: contentId,
        updatedAt: serverTimestamp()
      });
      return true;
    } catch (error) {
      console.error('Error linking content to media:', error);
      throw error;
    }
  }
}

export const flipCardService = new FlipCardService();